/**
 * Compiled macro package (`.pcmc` when zipped): what Playjector plays. `compiled.json` holds
 * integers only (normalized coordinates are 0..65535) and no timestamps, so identical
 * compile inputs always produce byte-identical canonical JSON.
 */
import { z } from 'zod';
import { macroVisualDigestSchema } from '../macroCalibration/common';
import { playbackBindingSchema, visualArtifactCertificationSchema, visualPlaybackPolicySchema } from '../macroExecution';
import { MACRO_VISUAL_PROFILE } from '../macroProfile';
import { macroNodeKindSchema, macroOnFailureSchema, macroTriggerSchema } from '../macroTree';
import { canonicalJson, sha256HexSchema } from './canonicalJson';
import { MACRO_PACKAGE_LIMITS } from './limits';
import { AUTHORING_MACRO_COMPILER_VERSION, COMMON_MACRO_COMPILER_VERSION, compiledProvenanceSchema } from './pipelineContracts';
import { macroMatchingModeSchema } from './matchingMode';

const t = z.number().int().min(0).max(600_000);
const n = z.number().int().min(0).max(65_535);
const i16 = z.number().int().min(-32768).max(32767);
const vk = z.number().int().min(1).max(254);
const button = z.number().int().min(0).max(4);
const contact = z.number().int().min(0).max(9);

export const COMPILED_OPS = ['kd', 'ku', 'mr', 'ma', 'md', 'mu', 'mw', 'td', 'tm', 'tu', 'gs'] as const;
export type CompiledOp = typeof COMPILED_OPS[number];

const eventSchemas = {
  kd: z.tuple([t, z.literal('kd'), vk]), ku: z.tuple([t, z.literal('ku'), vk]),
  mr: z.tuple([t, z.literal('mr'), i16, i16]), ma: z.tuple([t, z.literal('ma'), n, n]),
  md: z.tuple([t, z.literal('md'), button]), mu: z.tuple([t, z.literal('mu'), button]),
  /** [dy, dx] wheel units. */
  mw: z.tuple([t, z.literal('mw'), i16, i16]),
  td: z.tuple([t, z.literal('td'), contact, n, n]), tm: z.tuple([t, z.literal('tm'), contact, n, n]),
  tu: z.tuple([t, z.literal('tu'), contact]),
  gs: z.tuple([t, z.literal('gs'), z.number().int().min(0).max(3), z.number().int().min(0).max(0xffff),
    z.number().int().min(0).max(255), z.number().int().min(0).max(255), i16, i16, i16, i16]),
};
export type CompiledEvent = z.infer<typeof eventSchemas[keyof typeof eventSchemas]>;

/** Route by opcode once, then apply the exact tuple schema (including every range and arity check). */
export const compiledEventSchema = z.unknown().transform((value, context): CompiledEvent => {
  const op = Array.isArray(value) ? value[1] : undefined;
  if (typeof op !== 'string' || !Object.prototype.hasOwnProperty.call(eventSchemas, op)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Unknown compiled event opcode or invalid event tuple' });
    return z.NEVER;
  }
  const parsed = eventSchemas[op as CompiledOp].safeParse(value);
  if (parsed.success) return parsed.data;
  for (const issue of parsed.error.issues) context.addIssue(issue);
  return z.NEVER;
});

export const compiledScriptSchema = z.object({ durationMs: t, events: z.array(compiledEventSchema).max(10_000) }).strict();
export type CompiledScript = z.infer<typeof compiledScriptSchema>;

export const compiledNodeSchema = z.object({ id: z.string().uuid(), name: z.string().min(1).max(80),
  kind: macroNodeKindSchema, children: z.array(z.string().uuid()), onFailure: macroOnFailureSchema,
  match: z.object({ bindings: z.array(z.string().min(1).max(128)).min(1).max(64),
    matchThresholdPercent: z.number().int().min(0).max(100), region: z.object({ x: z.number().int(), y: z.number().int(),
      width: z.number().int().positive(), height: z.number().int().positive() }).strict().nullable() }).strict().nullable(),
  wait: z.object({ intervalMs: z.number().int().positive(), timeoutMs: z.number().int().positive(), enabled: z.boolean() }).strict().nullable(),
  postDelayMs: z.number().int().min(0),
  script: compiledScriptSchema.nullable(),
  requirements: z.object({ targets: z.array(z.enum(['keyboard', 'mouse', 'touch', 'gamepad'])).max(4),
    gamepadSlots: z.array(z.number().int().min(0).max(3)).max(4), maxHeldKeys: z.number().int().min(0).max(6),
    maxContacts: z.number().int().min(0).max(10) }).strict() }).strict();
export type CompiledNode = z.infer<typeof compiledNodeSchema>;

export const compiledBindingSchema = playbackBindingSchema.extend({ bindingId: z.string().min(1).max(128),
  matchThresholdPercent: z.number().int().min(0).max(100),
  authoredRegionThresholdPercent: z.number().int().min(0).max(100).optional() }).superRefine((binding, context) => {
  if (binding.authoredRegionThresholdPercent !== undefined && binding.authoredRegionThresholdPercent !== binding.matchThresholdPercent) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['authoredRegionThresholdPercent'],
      message: 'Authored region cutoff must agree with the effective binding threshold' });
  }
});
export type CompiledBinding = z.infer<typeof compiledBindingSchema>;

export const compiledMacroPackageSchema = z.object({ format: z.literal('playcast.macro.compiled'), formatVersion: z.literal(2, {
  errorMap: () => ({ message: 'legacy_macro_requires_rerecording: compiled v2 is required' }) }),
  campaignId: z.string().min(1).max(128), trigger: macroTriggerSchema,
  compilerVersion: z.enum([COMMON_MACRO_COMPILER_VERSION, AUTHORING_MACRO_COMPILER_VERSION]),
  intent: z.enum(['execute', 'observe']), provenance: compiledProvenanceSchema,
  evidence: z.object({ file: z.literal('evidence/index.json'), sha256: sha256HexSchema,
    bytes: z.number().int().positive().safe() }).strict(),
  sourceDigest: sha256HexSchema, profile: z.literal(MACRO_VISUAL_PROFILE), artifactDigest: macroVisualDigestSchema,
  certification: visualArtifactCertificationSchema, policy: visualPlaybackPolicySchema,
  matchingMode: macroMatchingModeSchema.optional(),
  geometry: z.object({ width: z.number().int().positive(), height: z.number().int().positive() }).strict(),
  refs: z.array(z.object({ file: z.string().regex(/^refs\/page-\d{1,3}\.bin$/), sha256: sha256HexSchema,
    bytes: z.number().int().positive() }).strict()).max(512),
  bindings: z.array(compiledBindingSchema).max(16_384),
  rootNodeId: z.string().uuid(), nodes: z.array(compiledNodeSchema).min(1).max(4_096) }).strict()
  .superRefine((value, context) => {
    const bindingCount = value.bindings.length;
    if (value.certification.expectedBindings !== bindingCount || value.certification.coveredBindings !== bindingCount
      || value.certification.readyBindings !== bindingCount || (bindingCount === 0 ? value.refs.length !== 0 : value.refs.length === 0)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['certification'], message: 'Compiled reference and binding coverage must agree with certification' });
    }
    const bindingIds = new Set(value.bindings.map(binding => binding.bindingId));
    if (value.nodes.some(node => node.match?.bindings.some(bindingId => !bindingIds.has(bindingId)))) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['nodes'], message: 'Compiled matches must name calibrated bindings' });
    }
    const identity = value.provenance.sourceIdentity;
    if (identity.campaignId !== value.campaignId || identity.trigger !== value.trigger || identity.graphDigest !== value.sourceDigest
      || value.certification.variant !== 'source-graph' || value.artifactDigest !== value.certification.artifactDigest
      || canonicalJson(identity) !== canonicalJson(value.certification.sourceIdentity ?? null)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['provenance'], message: 'Compiled provenance and certification must name the same source' });
    }
    if (value.intent === 'observe' && value.nodes.some(node => node.script !== null || node.requirements.targets.length > 0
      || node.requirements.gamepadSlots.length > 0 || node.requirements.maxHeldKeys !== 0 || node.requirements.maxContacts !== 0)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['nodes'], message: 'Observe packages cannot carry scripts or device requirements' });
    }
  });
export type CompiledMacroPackage = z.infer<typeof compiledMacroPackageSchema>;

export type CompiledScriptIssueCode = 'unsorted' | 'unbalanced_hold' | 'unmatched_release' | 'too_many_keys'
  | 'too_many_contacts' | 'move_without_down' | 'gamepad_not_neutral' | 'duration_too_short';
export interface CompiledScriptIssue { index: number; code: CompiledScriptIssueCode }

/** Script invariants; Playjector's decoder enforces the same table. */
export function validateCompiledScript(script: CompiledScript): CompiledScriptIssue[] {
  const issues: CompiledScriptIssue[] = [];
  const keys = new Set<number>(); const buttons = new Set<number>(); const contacts = new Set<number>();
  const gamepads = new Map<number, boolean>();
  let previousTime = 0;
  script.events.forEach((event, index) => {
    if (event[0] < previousTime) issues.push({ index, code: 'unsorted' });
    previousTime = Math.max(previousTime, event[0]);
    switch (event[1]) {
      case 'kd': press(keys, event[2], index, issues); if (keys.size > MACRO_PACKAGE_LIMITS.keyboardMaxHeld) issues.push({ index, code: 'too_many_keys' }); break;
      case 'ku': release(keys, event[2], index, issues); break;
      case 'md': press(buttons, event[2], index, issues); break;
      case 'mu': release(buttons, event[2], index, issues); break;
      case 'td': press(contacts, event[2], index, issues); if (contacts.size > MACRO_PACKAGE_LIMITS.touchMaxContacts) issues.push({ index, code: 'too_many_contacts' }); break;
      case 'tm': if (!contacts.has(event[2])) issues.push({ index, code: 'move_without_down' }); break;
      case 'tu': release(contacts, event[2], index, issues); break;
      case 'gs': gamepads.set(event[2], event.slice(3).every(value => value === 0)); break;
      default: break;
    }
  });
  const end = script.events.length;
  if (keys.size > 0 || buttons.size > 0 || contacts.size > 0) issues.push({ index: end, code: 'unbalanced_hold' });
  for (const neutral of gamepads.values()) if (!neutral) issues.push({ index: end, code: 'gamepad_not_neutral' });
  if (script.durationMs < previousTime) issues.push({ index: end, code: 'duration_too_short' });
  return issues;
}

function press(held: Set<number>, id: number, index: number, issues: CompiledScriptIssue[]): void {
  if (held.has(id)) issues.push({ index, code: 'unbalanced_hold' });
  held.add(id);
}

function release(held: Set<number>, id: number, index: number, issues: CompiledScriptIssue[]): void {
  if (!held.delete(id)) issues.push({ index, code: 'unmatched_release' });
}
